import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, X } from 'lucide-react';
import { T, Lang } from '../lib/i18n';
import { HexagonBg } from './HexagonBg';
export function Hero({
  lang
}: {
  lang: Lang;
}) {
  const t = T[lang].hero;
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section id="inicio" className="relative bg-white pt-16 pb-24 overflow-hidden" data-id="element-118">
      <HexagonBg data-id="element-119" />
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-blue-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-120"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10" data-id="element-121">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-122">
          {/* Left Content */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="lg:col-span-7" data-id="element-123">
            <div className="inline-flex items-center space-x-2 bg-brand-blue-primary/10 border border-brand-blue-primary/20 text-brand-blue-primary px-4 py-1.5 rounded-full text-sm font-medium mb-8" data-id="element-124">
              <div className="w-2 h-2 rounded-full bg-brand-blue-primary animate-blink" data-id="element-125"></div>
              <span data-id="element-126">{t.tag}</span>
            </div>

            <h1 className="font-display font-extrabold text-[clamp(2.8rem,5vw,5rem)] leading-[1.05] tracking-[-2px] mb-6" data-id="element-127">
              <div className="text-brand-navy-dark" data-id="element-128">{t.h1_1}</div>
              <div className="bg-gradient-brand bg-clip-text text-transparent" data-id="element-129">
                {t.h1_2}
              </div>
              <div className="text-stroke-blue" data-id="element-130">{t.h1_3}</div>
            </h1>

            <p className="text-brand-text-secondary font-light text-base md:text-lg leading-[1.9] max-w-[500px] mb-8" data-id="element-131">
              {t.subtitle}
            </p>

            <div className="space-y-4 mb-10" data-id="element-132">
              {[t.pain1, t.pain2, t.pain3].map((pain, i) => <div key={i} className="flex items-start space-x-3" data-id="element-133">
                  <X className="w-5 h-5 text-red-500 shrink-0 mt-1" data-id="element-134" />
                  <p className="text-brand-text-muted text-sm md:text-base" data-id="element-135">
                    {pain}
                  </p>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-136">
              <button onClick={() => scrollTo('paquetes')} className="bg-gradient-button text-white px-8 py-4 rounded-lg font-semibold shadow-[0_4px_30px_rgba(30,95,212,0.35)] hover:shadow-[0_6px_40px_rgba(30,95,212,0.45)] transition-all hover:-translate-y-1 relative overflow-hidden group" data-id="element-137">
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out" data-id="element-138"></div>
                {t.btnPrimary}
              </button>
              <button onClick={() => scrollTo('contacto')} className="border border-brand-blue-primary text-brand-blue-primary px-8 py-4 rounded-lg font-semibold hover:bg-brand-blue-primary/5 transition-colors" data-id="element-139">
                {t.btnGhost}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-brand-blue-primary/10" data-id="element-140">
              {[{
              n: t.stat1,
              l: t.stat1L
            }, {
              n: t.stat2,
              l: t.stat2L
            }, {
              n: t.stat3,
              l: t.stat3L
            }, {
              n: t.stat4,
              l: t.stat4L
            }].map((stat, i) => <div key={i} data-id="element-141">
                  <div className="font-display font-extrabold text-2xl md:text-3xl text-brand-blue-primary mb-1" data-id="element-142">
                    {stat.n}
                  </div>
                  <div className="font-body text-[0.68rem] uppercase text-brand-text-muted font-medium tracking-wider" data-id="element-143">
                    {stat.l}
                  </div>
                </div>)}
            </div>
          </motion.div>

          {/* Right Floating Card */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="lg:col-span-5 relative" data-id="element-144">
            <div className="bg-white border border-brand-blue-primary/15 rounded-[20px] shadow-[0_20px_60px_rgba(15,28,63,0.12),0_0_0_1px_rgba(30,95,212,0.05)] overflow-hidden relative" data-id="element-145">
              <div className="h-[3px] w-full bg-gradient-to-r from-brand-blue-primary via-brand-blue-bright to-brand-gold-champagne" data-id="element-146"></div>

              <div className="absolute bottom-0 right-0 w-32 h-32 opacity-5 pointer-events-none" data-id="element-147">
                <svg viewBox="0 0 60 104" fill="#1e5fd4" data-id="element-148">
                  <path d="M30 104L0 86.679V52L30 34.679L60 52V86.679L30 104ZM30 100.536L57 84.947V53.732L30 38.143L3 53.732V84.947L30 100.536ZM30 69.321L0 52V17.321L30 0L60 17.321V52L30 69.321ZM30 65.857L57 50.268V19.053L30 3.464L3 19.053V50.268L30 65.857Z" data-id="element-149" />
                </svg>
              </div>

              <div className="p-8" data-id="element-150">
                <div className="inline-block bg-brand-blue-primary/10 text-brand-blue-primary text-xs font-bold px-3 py-1 rounded-full mb-4" data-id="element-151">
                  {t.cardLabel}
                </div>

                <h3 className="font-display font-bold text-xl text-brand-navy-dark mb-6" data-id="element-152">
                  {t.cardTitle}
                </h3>

                <div className="space-y-3 mb-8" data-id="element-153">
                  {[t.cardF1, t.cardF2, t.cardF3, t.cardF4, t.cardF5].map((feat, i) => <div key={i} className="flex items-center space-x-3" data-id="element-154">
                        <CheckCircle2 className="w-5 h-5 text-brand-blue-primary shrink-0" data-id="element-155" />
                        <span className="text-brand-text-secondary text-sm font-medium" data-id="element-156">
                          {feat}
                        </span>
                      </div>)}
                </div>

                <div className="flex items-end space-x-3 mb-4" data-id="element-157">
                  <span className="text-brand-text-muted text-sm pb-2" data-id="element-158">
                    {t.cardPriceOnly}
                  </span>
                  <span className="font-display font-black text-4xl md:text-5xl text-brand-blue-primary" data-id="element-159">
                    $800
                  </span>
                  <span className="text-brand-text-muted line-through text-lg pb-1.5" data-id="element-160">
                    $2,050
                  </span>
                </div>

                <div className="bg-red-500/10 border border-red-500/20 text-red-700 text-sm font-semibold py-2 px-4 rounded-lg mb-6 text-center" data-id="element-161">
                  {t.cardUrgency}
                </div>

                <button onClick={() => scrollTo('contacto')} className="w-full bg-gradient-button text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 mb-4" data-id="element-162">
                  {t.cardBtn}
                </button>

                <p className="text-center text-[0.7rem] text-brand-text-muted" data-id="element-163">
                  {t.cardNote}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}